import React from 'react';
import { useAppDispatch } from 'hooks/redux';
import { isEqual } from 'lodash';
import { setElementValues } from 'store/rfiSlice';
import { answerValueTypes, elementTypes, IElementProps } from 'types/RfiTypes';
import { TextArea, TextAreaChangeEvent } from '@progress/kendo-react-inputs';
import { Hint } from '@progress/kendo-react-labels';

const RfiTextArea = (props: IElementProps) => {
  // Constants
  const max = 4000;

  let defaultValue = props.element.answers
    ? props.element.answers[0]?.value
    : "";

  // Hooks
  const dispatch = useAppDispatch();

  // State
  const [value, setValue] = React.useState(defaultValue);

  // Handlers
  const handleChange = (e: TextAreaChangeEvent) => {
    dispatch(
      setElementValues({
        elementId: props.element.elementId,
        values: [
          {
            value: e.target.value?.toString(),
            answerValueType: answerValueTypes.DEFAULT,
          },
        ],
      })
    );

    setValue(e.target.value);
  };

  // Render
  if (props.element.elementType === elementTypes.FIELD) {
    if (props.readOnly) {
      return (
        <div className="form-group row">
          <div className="col-lg-3 col-form-label">
            {props.element.fieldLabel}
          </div>
          <div className="col-lg-8 fieldElement">{value}</div>
        </div>
      );
    } else
      return (
        <div>
          <div className="form-group row">
            <div className="col-lg-3 col-form-label">
              <label
                className="col-form-label"
                htmlFor={`textarea-${props.element.elementId}`}
              >
                {props.element.fieldLabel}{" "}
                {props.overrideRequired === undefined ? (
                  props.element.requiredResponse ? (
                    <span className="requiredField">(required)</span>
                  ) : (
                    ""
                  )
                ) : props.overrideRequired === true ? (
                  <span className="requiredField">(required)</span>
                ) : (
                  ""
                )}
              </label>
            </div>
            <div className="col-lg-8 fieldElement">
              <TextArea
                value={value}
                onChange={handleChange}
                maxLength={max}
                rows={7}
                id={`textarea-${props.element.elementId}`}
              />
              <Hint direction={"end"}>
                {value?.length ?? 0} / {max}
              </Hint>
            </div>
          </div>
        </div>
      );
  } else {
    if (props.readOnly) {
      return (
        <>
          <div className="col-form-label">
            {`${props.questionNumber}.`} {props.element.fieldLabel}{" "}
            {props.overrideRequired === undefined ? (
              props.element.requiredResponse ? (
                <span>*</span>
              ) : (
                ""
              )
            ) : props.overrideRequired === true ? (
              <span>*</span>
            ) : (
              ""
            )}
          </div>
          <div style={{ whiteSpace: "pre-line" }}>
            {props.showValue ? (
              value
            ) : (
              <div style={{ marginLeft: "20px", marginRight: "10px" }}>
                <textarea
                  id={`read-only-${props.element.elementId}`}
                  className="form-control"
                  disabled
                  value={value}
                />
                <br />
              </div>
            )}
          </div>
        </>
      );
    } else
      return (
        <>
          <fieldset>
            <legend className="visuallyHidden">
              {props.element.fieldLabel}
            </legend>
            <label
              className="col-form-label"
              htmlFor={`textarea-${props.element.elementId}`}
            >
              <div>
                <div>
                  {`${props.questionNumber}.`} {props.element.fieldLabel}{" "}
                  {props.overrideRequired === undefined ? (
                    props.element.requiredResponse ? (
                      <span className="requiredField">(required)</span>
                    ) : (
                      ""
                    )
                  ) : props.overrideRequired === true ? (
                    <span className="requiredField">(required)</span>
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </label>
            <div>
              <TextArea
                value={value}
                onChange={handleChange}
                maxLength={max}
                rows={7}
                id={`textarea-${props.element.elementId}`}
              />
              <Hint direction={"end"}>
                {value?.length ?? 0} / {max}
              </Hint>
            </div>
          </fieldset>
        </>
      );
  }
};

// Only re-render if props changedF
const areEqual = (prevProps: IElementProps, nextProps: IElementProps) => {
  return (
    !prevProps.rerender &&
    isEqual(prevProps.element, nextProps.element) &&
    isEqual(prevProps.designMode, nextProps.designMode) &&
    isEqual(prevProps.readOnly, nextProps.readOnly)
  );
};

const TextAreaElement = React.memo(RfiTextArea, areEqual);

export default TextAreaElement;
